import { useEffect, useState } from "react";
import { makeStyles } from "tss-react/mui";
import { ICONS_WEB, MUI_DATAGRID_KEY } from "@miplatform/constants";
import { toKebabCase } from "@miplatform/utils";
import * as mdi from "@mdi/js";
import { Box, Grid } from "@mui/material";
import {
  DataGridPremium as DataGrid,
  GridColumnMenu,
  useGridApiRef,
} from "@mui/x-data-grid-premium";
import { LicenseInfo } from "@mui/x-license";
import { MiButton } from "../MiButton/MiButton";
import { MiTableRowActions } from "./MiTableRowActions/MiTableRowActions";

LicenseInfo.setLicenseKey(MUI_DATAGRID_KEY);

export function MiTable({
  tableContainerStyle,
  columns,
  getRowActions,
  actionIconStyle,
  moreVertColor,
  miCheckboxSelection,
  isMiCheckboxEnabled,
  initialState,
  onMiSelectionChange,
  miSelectedRowIds,
  apiRef,
  pagination,
  columnVisibilityModel,
  onColumnVisibilityModelChange,
  leftToolbarComponent,
  rightToolbarComponent,
  hideFooter = true,
  pageSizeOptions = [10, 25, 50, 100],
  autoRowHeight = false,
  ...rest
}) {
  const [columnsApiRef, setColumnsApiRef] = useState([]);
  const [colDefs, setColDefs] = useState(columns);
  const [selectedActionRow, setSelectedActionRow] = useState(null);
  const [visibleColumns, setVisibleColumns] = useState(
    columnVisibilityModel || {}
  );
  const [anchorEl, setAnchorEl] = useState(null);
  const open = Boolean(anchorEl);
  const { classes } = useStyles({ autoRowHeight });
  const apiRefLocal = useGridApiRef();
  const apiRefInstance = apiRef || apiRefLocal;
  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleOpen = (e, row) => {
    setSelectedActionRow(row);
    setAnchorEl(e.currentTarget);
  };

  const handleActionsIconSelect = (e, row) => {
    if (row?.id === selectedActionRow?.id) {
      setSelectedActionRow(null);
      setAnchorEl(null);
    } else {
      e.stopPropagation();
      handleOpen(e, row);
    }
  };

  useEffect(() => {
    const orderedColumns = columns.sort(
      (a, b) => columnsApiRef.indexOf(a.field) - columnsApiRef.indexOf(b.field)
    );
    const updatedDefs = [...orderedColumns];

    if (getRowActions) {
      updatedDefs.push({
        headerName: "",
        field: "",
        sortable: false,
        filterable: false,
        hideable: false,
        disableColumnMenu: true,
        resizable: false,
        disableExport: true,
        align: "right",
        renderCell: ({ row = {}, rowNode }) => {
          const rowInfo = Object.keys(row).length ? row : rowNode;
          if (rowInfo?.type === "group") return null;
          return (
            <Grid
              onClick={(e) => handleActionsIconSelect(e, rowInfo)}
              style={
                row.disabledMoreVert
                  ? { pointerEvents: "none", opacity: "0.5" }
                  : { cursor: "pointer", color: moreVertColor }
              }
            >
              <MiButton
                style={actionIconStyle}
                onClick={(e) => {
                  handleOpen(e, row);
                }}
                startIcon={{
                  path: mdi[ICONS_WEB.MENU],
                  iconSize: 1,
                }}
                data-testid={`menu-button-${toKebabCase(rowInfo.name)}`}
              />
              {rowInfo.id === selectedActionRow?.id && (
                <MiTableRowActions
                  {...{
                    getRowActions,
                    selectedActionRow,
                    handleClose,
                    anchorEl,
                    open,
                  }}
                  rowKey={rowInfo.id}
                />
              )}
            </Grid>
          );
        },
      });
    }
    setColDefs(updatedDefs);
  }, [selectedActionRow, miSelectedRowIds, columns]);

  useEffect(() => {
    setVisibleColumns(columnVisibilityModel);
  }, [columnVisibilityModel]);

  const handleVisiblityChange = (newModel) => {
    if (onColumnVisibilityModelChange) {
      onColumnVisibilityModelChange(newModel);
    } else {
      setVisibleColumns(newModel);
    }
  };

  const onColumnOrderChange = () => {
    const { columns } = apiRefInstance.current.exportState();
    setColumnsApiRef(columns.orderedFields);
  };

  const renderDataGrid = () => {
    if (!columns) {
      return null;
    }
    return (
      <Box style={{ display: "flex", height: "100%" }}>
        <Box
          style={{ ...tableContainerStyle, flexGrow: 1, minWidth: 0 }}
          data-testid="mi-table"
        >
          <DataGrid
            className={classes.tableContainer}
            {...rest}
            apiRef={apiRefInstance}
            columns={colDefs}
            disableSelectionOnClick
            hideFooter={!pagination && hideFooter}
            pagination={pagination}
            columnVisibilityModel={visibleColumns}
            onColumnVisibilityModelChange={handleVisiblityChange}
            onColumnOrderChange={onColumnOrderChange}
            showColumnVerticalBorder={false}
            disableColumnResize
            disableVirtualization
            {...(autoRowHeight
              ? {
                  autoHeight: true,
                  getRowHeight: () => "auto",
                }
              : {})}
            initialState={{
              pagination: {
                paginationModel: {
                  pageSize: pageSizeOptions[1], // 25 rows per page by default
                },
              },
              ...initialState,
            }}
            pageSizeOptions={pageSizeOptions}
            slots={{
              columnMenu: (props) => (
                <GridColumnMenu
                  slots={{
                    columnMenuColumnsItem: null,
                    columnMenuPinningItem: null,
                    columnMenuAggregationItem: null,
                  }}
                  {...props}
                />
              ),
            }}
            slotProps={{
              row: {
                "data-testid": `MiTable-Row`,
              },
            }}
          />
        </Box>
      </Box>
    );
  };

  return renderDataGrid();
}

const useStyles = makeStyles()((theme, { autoRowHeight }) => ({
  tableContainer: {
    width: "100%",
    flex: 1,
    alignSelf: "stretch",
    "&.MuiDataGrid-root": {
      border: "none",
      fontSize: theme.fontSize.medium,
    },
    "& .MuiDataGrid-columnHeaderTitleContainer": {
      backgroundColor: theme.palette.common.white,
    },
    "& .MuiDataGrid-columnHeader": {
      borderRight: `1px solid ${theme.palette.divider}`,
      backgroundColor: theme.palette.common.white,
      "&[data-field='closeIcon']": {
        visibility: "hidden",
      },
    },
    "& .MuiDataGrid-columnSeparator": {
      backgroundColor: theme.palette.common.white,
      display: "none",
    },
    "& .MuiDataGrid-menuIcon": {
      visibility: "visible",
    },
    "& .MuiDataGrid-footerContainer": {
      backgroundColor: theme.palette.common.white,
      alignItems: "right",
      paddingLeft: 8,
    },
    "& .MuiDataGrid-columnHeaderTitle": {
      color: theme.palette.bluegrey.main,
      fontWeight: theme.fontWeight.bold,
    },
    "& .inActiveRow": {
      background: theme.palette.grey.light,
    },
    "& .MuiDataGrid-cell": {
      color: theme.palette.bluegrey.dark,
      fontWeight: theme.fontWeight.regular,
      fontSize: theme.fontSize.medium,
      display: "flex",
      alignItems: "center",
    },
    "& .inActiveRow .MuiDataGrid-cell": {
      color: theme.palette.grey.medium,
    },
    "& .groupedRow .MuiDataGrid-cell": {
      fontWeight: theme.fontWeight.semiBold,
    },
    "& .groupedRow .MuiIconButton-root": {
      fontSize: theme.fontSize.headerSmall,
    },
    "& .nestedGroupedRow .MuiDataGrid-cell": {
      fontWeight: theme.fontWeight.semiBold,
    },
    "& .MuiDataGrid-toolbarContainer .MuiButton-root": {
      color: theme.palette.primary.main,
    },
    "& .MuiDataGrid-aggregationColumnHeaderLabel": {
      visibility: "hidden",
    },
    "& .MuiDataGrid-columnHeader:focus": {
      outline: "none !important",
    },
    "& .MuiDataGrid-main": {
      minHeight: 200,
    },
    "& .MuiDataGrid-row": {
      "&:nth-of-type(even)": {
        background: "#FAFAFA",
      },
    },
    "& .MuiDataGrid-row.nestedGroupedRow": {
      background: theme.palette.primary.light,
      "&:hover": {
        background: theme.palette.primary.light,
      },
      "& .MuiDataGrid-cellCheckbox": {
        visibility: "hidden",
      },
    },
    "& .MuiDataGrid-row.groupedRow": {
      background: theme.palette.grey.accent,
      "&:hover": {
        background: theme.palette.grey.darkAccent,
      },
      "& .MuiDataGrid-cellCheckbox": {
        visibility: "hidden",
      },
    },
    ...(autoRowHeight
      ? {
          "& .MuiDataGrid-cell": {
            whiteSpace: "normal",
            lineHeight: "normal",
            padding: theme.spacing(2, 4),
            overflow: "auto",
          },
          "& .MuiDataGrid-row": {
            maxHeight: "none !important",
          },
          "& .MuiDataGrid-renderingZone": {
            maxHeight: "none !important",
          },
          "& .MuiDataGrid-virtualScroller": {
            height: "auto !important",
          },
        }
      : {}),
  },
}));
